import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { Navigation } from './components/Navigation';
import { Login } from './components/Login';
import { LanguageSelection } from './components/LanguageSelection';
import { LevelSelection } from './components/LevelSelection';
import { LessonList } from './components/LessonList';
import { AccountInfo } from './components/AccountInfo';
import { Settings } from './components/Settings';
import { LessonViewer } from './components/LessonViewer';
import { PracticePage } from './components/PracticePage';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { Guide } from './components/Guide';
import { ContinueLastLessonPrompt } from './components/ContinueLastLessonPrompt';
import { CustomLessonsList } from './components/CustomLessonsList';
import { supabase } from './utils/supabase';
import { type UserProfile, type LanguageLevel } from './types';
import { handleOAuthCallback } from './utils/auth';
import { setupIOSBridge, isRunningInIOSApp } from './utils/iosBridge';

function LessonViewerWrapper() {
  const { lessonId } = useParams();
  return <LessonViewer key={lessonId} />;
}

function PracticePageWrapper() {
  const { lessonId } = useParams();
  return <PracticePage key={lessonId} />;
}

function useIsOnLessonPage() {
  const location = useLocation();
  return location.pathname.startsWith('/lesson/');
}

function AppContent({
  user,
  currentView,
  setCurrentView,
  showContinuePrompt,
  setShowContinuePrompt,
  showGuide,
  setShowGuide,
  isLevelLoading,
  pageKey,
  handleLanguageSelect,
  handleLevelSelect,
  handleSignOut,
  handleLogin
}: {
  user: UserProfile | null;
  currentView: string;
  setCurrentView: (view: string) => void;
  showContinuePrompt: boolean;
  setShowContinuePrompt: (show: boolean) => void;
  showGuide: boolean;
  setShowGuide: (show: boolean) => void;
  isLevelLoading: boolean;
  pageKey: number;
  handleLanguageSelect: (targetLang: string, nativeLang: string) => void;
  handleLevelSelect: (level: LanguageLevel) => void;
  handleSignOut: () => void;
  handleLogin: (user: UserProfile) => void;
}) {
  const isOnLessonPage = useIsOnLessonPage();

  return (
    <div className="min-h-screen bg-gray-50" key={pageKey}>
      {user && currentView !== 'login' && (
        <Navigation
          user={user}
          showGuide={showGuide}
          setShowGuide={setShowGuide}
          onLanguageSelect={() => setCurrentView('language')}
          onLevelSelect={() => setCurrentView('level')}
          onSignOut={handleSignOut}
          onViewAccount={() => setCurrentView('account')}
          onViewSettings={() => setCurrentView('settings')}
        />
      )}

      {showContinuePrompt && user && !isOnLessonPage && user.currentLevel !== 'Custom' && (
        <ContinueLastLessonPrompt
          userId={user.uid}
          level={user.currentLevel || 'A1'}
          onClose={() => setShowContinuePrompt(false)}
          nativeLanguage={user.nativeLanguage}
        />
      )}

      {showGuide && (
        <Guide
          onClose={() => setShowGuide(false)}
          isFirstTime={!user?.hasSeenGuide}
          userId={user?.uid}
          language={user?.nativeLanguage || 'English'}
        />
      )}

      <Routes>
        <Route path="/" element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/lessons" replace />} />

        <Route path="/lessons" element={
          user ? (
            <div className="container mx-auto px-4 py-8">
              {isLevelLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : currentView === 'language' && (
                <LanguageSelection onSelect={handleLanguageSelect} />
              )}

              {currentView === 'level' && (
                <LevelSelection
                  onSelect={handleLevelSelect}
                  defaultLevel={user.currentLevel || 'A1'}
                  nativeLanguage={user.nativeLanguage}
                />
              )}

              {currentView === 'lessons' && user.currentLevel !== 'Custom' && (
                <LessonList level={user.currentLevel || 'A1'} />
              )}

              {currentView === 'lessons' && user.currentLevel === 'Custom' && (
                <CustomLessonsList
                  userId={user.uid}
                  nativeLanguage={user.nativeLanguage}
                  isPremium={user.isPremium}
                  onSelectLesson={(lessonId) => {
                    window.location.href = `/lesson/${lessonId}`;
                  }}
                  onBack={() => setCurrentView('level')}
                />
              )}
              {currentView === 'settings' && (
                <Settings user={user} onLanguageSelect={() => setCurrentView('language')} />
              )}
            </div>
          ) : (
            <Navigate to="/" replace />
          )
        } />

        <Route
          path="/lesson/:lessonId"
          element={
            user ? <LessonViewerWrapper /> : <Navigate to="/" replace />
          }
        />
        <Route
          path="/lesson/:lessonId/practice"
          element={
            user ? <PracticePageWrapper /> : <Navigate to="/" replace />
          }
        />
        <Route
          path="/admin"
          element={
            user?.isAdmin ? <AdminDashboard key={`admin-${pageKey}`} /> : <Navigate to="/" replace />
          }
        />
      </Routes>
    </div>
  );
}

function App() {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [currentView, setCurrentView] = useState<string>('login');
  const [loading, setLoading] = useState(true);
  const [showGuide, setShowGuide] = useState<boolean>(false);
  const authInitializedRef = useRef(false);
  const loadingTimeoutRef = useRef<number | null>(null);
  const [initialLevelLoaded, setInitialLevelLoaded] = useState(false);
  const [showContinuePrompt, setShowContinuePrompt] = useState(false);
  const [isLevelLoading, setIsLevelLoading] = useState(true);
  const [pageKey, setPageKey] = useState(Date.now()); // Add a key to force full component remount when needed

  useEffect(() => {
    // Only initialize auth once to prevent infinite loops
    if (authInitializedRef.current) {
      return;
    }
    
    // Set a loading timeout to prevent infinite loading state
    loadingTimeoutRef.current = window.setTimeout(() => {
      console.log('Auth initialization timeout reached, resetting state');
      setLoading(false);
      setIsLevelLoading(false);
      setCurrentView('login');
      authInitializedRef.current = false; // Allow re-initialization
    }, 10000); // 10 second timeout

    authInitializedRef.current = true;

    // Function to create a user profile from session data
    const createUserProfile = (user: any, hasSeenGuide: boolean = false): UserProfile => {
      const isAdmin = ['alinickmaleki@gmail.com', 'alinickmaleki@ymail.com'].includes(user.email || '');
      
      // Get level from localStorage or default to A1
      const storedLevel = localStorage.getItem('currentLevel');
      const level = (storedLevel && storedLevel !== '') ? storedLevel : 'A1';
      
      return {
        uid: user.id,
        email: user.email || '',
        targetLanguage: localStorage.getItem('targetLanguage') || 'Dutch',
        nativeLanguage: localStorage.getItem('nativeLanguage') || 'English',
        currentLevel: level,
        lessonsCompleted: [],
        dateJoined: new Date(),
        isAdmin: isAdmin,
        hasSeenGuide: hasSeenGuide
      };
    };

    const initializeAuth = async () => {
      try {
        // Check for OAuth code in URL and handle it
        // Clear any stale sessions from localStorage
        Object.keys(localStorage).forEach(key => {
          if ((key.startsWith('sb-') && key.includes('auth-token')) || 
              (key.startsWith('supabase.auth.token'))) {
            try {
              // Check if token is expired
              const tokenData = JSON.parse(localStorage.getItem(key) || '{}');
              if (tokenData.expiresAt && tokenData.expiresAt < Date.now() / 1000) {
                console.log('Removing expired token', key);
                localStorage.removeItem(key);
              }
            } catch (e) {
              // If we can't parse the token data, remove it anyway
              localStorage.removeItem(key);
            }
          }
        });
        
        const handleOAuthRedirect = async () => {
          const urlParams = new URLSearchParams(window.location.search);
          const code = urlParams.get('code');
          
          if (code) {
            console.log('OAuth code detected in App, handling redirect...', code);
            
            try {
              // Use the handler function
              const newUser = await handleOAuthCallback(code);

              if (newUser) {
                console.log('Successfully authenticated with Google');

                // Clean up URL by removing the code parameter
                window.history.replaceState({}, document.title, window.location.pathname);

                // Create or update profile in database
                try {
                  // First try update to avoid duplicates
                  const { error: updateError } = await supabase
                    .from('profiles')
                    .update({
                      email: newUser.email,
                      target_language: newUser.targetLanguage,
                      native_language: newUser.nativeLanguage,
                      current_level: newUser.currentLevel || 'A1',
                      is_admin: newUser.isAdmin,
                      has_seen_guide: newUser.hasSeenGuide || false
                    })
                    .eq('user_id', newUser.uid);
                    
                  if (updateError) {
                    console.log('Profile update failed, trying upsert:', updateError);
                    
                    // If update fails, try upsert
                    const { error: upsertError } = await supabase
                      .from('profiles')
                      .upsert({
                        user_id: newUser.uid,
                        email: newUser.email,
                        target_language: newUser.targetLanguage,
                        native_language: newUser.nativeLanguage,
                        current_level: newUser.currentLevel || 'A1',
                        is_admin: newUser.isAdmin,
                        has_seen_guide: newUser.hasSeenGuide || false
                      }, { 
                        onConflict: 'user_id',
                        ignoreDuplicates: false
                      });
                    
                    if (upsertError) {
                      console.error('Error upserting profile:', upsertError);
                    }
                  }
                  
                } catch (profileError) {
                  console.error('Error updating profile:', profileError);
                }

                setUser(newUser);
                setCurrentView('lessons');
                setInitialLevelLoaded(true);
                setIsLevelLoading(false);
                return true;
              }
            } catch (error) {
              console.error('Error handling OAuth redirect in App:', error);
              
              setCurrentView('login');
              setIsLevelLoading(false);
              setLoading(false);
              return true;
            }
          }
          return false;
        };
        
        // Handle OAuth redirect if present
        const redirectHandled = await handleOAuthRedirect();
        if (redirectHandled) return;
        
        // Clear stale sessions but preserve auth tokens
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('sb-') && key.includes('_') && !key.includes('-auth-token')) {
            const timestamp = parseInt(key.split('_')[1]);
            if (Date.now() - timestamp > 24 * 60 * 60 * 1000) {
              localStorage.removeItem(key);
            }
          }
        });
        
        // Get current session
        const { data: { session } } = await supabase.auth.getSession();

        if (!session?.user) {
          setCurrentView('login');
          setIsLevelLoading(false);
          setLoading(false);
          return;
        }
        
        // Get user profile from database - this is the source of truth
        try {
          const { data: profile, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('user_id', session.user.id)
            .maybeSingle();
          
          if (error) {
            console.error('Error fetching profile:', error);
            throw error;
          }
          
          if (profile) {
            // Create user profile from database data
            const newUser: UserProfile = {
              uid: session.user.id,
              email: session.user.email || '',
              targetLanguage: profile.target_language || 'Dutch',
              nativeLanguage: profile.native_language || 'English',
              currentLevel: profile.current_level || 'A1',
              lessonsCompleted: [],
              dateJoined: new Date(profile.created_at),
              isAdmin: profile.is_admin || false,
              isPremium: profile.is_premium || profile.is_admin || false,
              hasSeenGuide: profile.has_seen_guide || false
            };
            
            // Update localStorage to match database values
            localStorage.setItem('targetLanguage', newUser.targetLanguage);
            localStorage.setItem('nativeLanguage', newUser.nativeLanguage);
            localStorage.setItem('currentLevel', newUser.currentLevel);
            localStorage.setItem('hasSeenGuide', String(newUser.hasSeenGuide));
            
            setUser(newUser);
            setCurrentView('lessons');
            setInitialLevelLoaded(true);
            setShowContinuePrompt(true);
            
            // Show guide if user hasn't seen it
            if (!newUser.hasSeenGuide) {
              console.log('User has not seen guide, showing it');
              setShowGuide(true);
            } else {
              console.log('User has seen guide, not showing it');
            }
          } else {
            // No profile found, create one with default values
            const isAdmin = ['alinickmaleki@gmail.com', 'alinickmaleki@ymail.com'].includes(session.user.email || '');
            const newUser: UserProfile = {
              uid: session.user.id,
              email: session.user.email || '',
              targetLanguage: 'Dutch',
              nativeLanguage: 'English',
              currentLevel: 'A1',
              lessonsCompleted: [],
              dateJoined: new Date(),
              isAdmin: isAdmin,
              hasSeenGuide: false
            };
            
            // Create profile in database
            try {
              await supabase
                .from('profiles')
                .upsert({
                  user_id: newUser.uid,
                  email: newUser.email,
                  target_language: newUser.targetLanguage,
                  native_language: newUser.nativeLanguage,
                  current_level: newUser.currentLevel,
                  is_admin: newUser.isAdmin,
                  has_seen_guide: newUser.hasSeenGuide
                }, {
                  onConflict: 'user_id',
                  ignoreDuplicates: false
                });
                
              // Update localStorage
              localStorage.setItem('targetLanguage', newUser.targetLanguage);
              localStorage.setItem('nativeLanguage', newUser.nativeLanguage);
              localStorage.setItem('currentLevel', newUser.currentLevel);
              localStorage.setItem('hasSeenGuide', String(newUser.hasSeenGuide));
              
              setUser(newUser);
              setCurrentView('lessons');
              setInitialLevelLoaded(true);
              setShowGuide(true);
            } catch (insertError) {
              console.error('Error creating profile:', insertError);
            }
          }
        } catch (error) {
          console.error('Error fetching profile:', error);
          
          // Fallback to basic profile with localStorage values
          const storedLevel = localStorage.getItem('currentLevel');
          const newUser = createUserProfile(session.user, localStorage.getItem('hasSeenGuide') === 'true');
          newUser.currentLevel = (storedLevel && storedLevel !== '') ? storedLevel : 'A1';
          
          setUser(newUser);
          setCurrentView('lessons');
          setInitialLevelLoaded(true);
        }
        
        setIsLevelLoading(false);
        setLoading(false);
        
      } catch (error) {
        console.error('Error initializing auth:', error);
        setCurrentView('login');
        setIsLevelLoading(false);
      } finally {
        setLoading(false);
      }
          // Clear loading timeout to prevent unnecessary state changes
          if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
            loadingTimeoutRef.current = null;
          }
    };

    initializeAuth();
    
    
    // Use a try-catch around the subscription to handle potential errors
    let subscription;
    try {
      const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
        console.log('Auth state changed:', event);
        
        if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
          console.log('User signed out or deleted');
          setUser(null);
          setCurrentView('login');
          
          localStorage.clear();
          
          // Allow reinitialization after sign out
          authInitializedRef.current = false;
        } else if (event === 'SIGNED_IN' && session?.user) {
          console.log('Auth state changed: SIGNED_IN');
          
          try {
            // Get profile from database - source of truth
          } catch (error) {
            console.error('Error handling SIGNED_IN event:', error);
          }
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          console.log('Auth state changed: TOKEN_REFRESHED');
          // Only update if we don't already have a user
          if (!user) {
            try {
              
              const newUser = createUserProfile(session.user);
              setUser(newUser);
              setCurrentView('lessons');
            } catch (error) {
              console.error('Error handling token refresh:', error);
            }
          }
        }
      });
      
      subscription = data.subscription;
    } catch (error) {
      console.error('Error setting up auth subscription:', error);
      // Ensure we still clear the loading state even if subscription fails
      setLoading(false);
      setIsLevelLoading(false);
    }

    return () => {
      // Clear any timeouts on cleanup
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      
      // Unsubscribe from auth changes if we have a subscription
      if (subscription) {
        try {
          subscription.unsubscribe();
        } catch (e) {
          console.error('Error unsubscribing from auth changes:', e);
        }
      }
    };
  }, []);

  useEffect(() => {
    if (user?.uid && isRunningInIOSApp()) {
      setupIOSBridge(user.uid);
    }
  }, [user?.uid]);

  const handleLogin = async (newUser: UserProfile) => {
    setUser(newUser);
    
    try {
      // Get the has_seen_guide value from localStorage if available
      const storedHasSeenGuide = localStorage.getItem('hasSeenGuide') === 'true';
      
      // Update or create profile in database - source of truth
      try {
        // First try to get existing profile
        const { data: existingProfile } = await supabase
          .from('profiles')
          .select('has_seen_guide, current_level')
          .eq('user_id', newUser.uid)
          .maybeSingle();
        
        let hasSeenGuideValue = newUser.hasSeenGuide;
        let currentLevelValue = newUser.currentLevel;
        
        // If profile exists, use its values unless we have overrides
        if (existingProfile) {
          hasSeenGuideValue = storedHasSeenGuide || existingProfile.has_seen_guide || false;
          currentLevelValue = localStorage.getItem('currentLevel') || existingProfile.current_level || 'A1';
        }
        
        // Update the user object with the determined values
        newUser.hasSeenGuide = hasSeenGuideValue;
        newUser.currentLevel = currentLevelValue;
        
        // Try to update existing profile
        const { error } = await supabase
          .from('profiles')
          .update({ 
            email: newUser.email,
            target_language: newUser.targetLanguage,
            native_language: newUser.nativeLanguage,
            current_level: newUser.currentLevel,
            is_admin: newUser.isAdmin,
            has_seen_guide: newUser.hasSeenGuide
          })
          .eq('user_id', newUser.uid);
          
        if (error) {
          console.log('Update failed, trying upsert:', error);
          
          // If update fails (likely profile doesn't exist), try upsert
          const { error: upsertError } = await supabase
            .from('profiles')
            .upsert({ 
              user_id: newUser.uid,
              email: newUser.email,
              target_language: newUser.targetLanguage,
              native_language: newUser.nativeLanguage,
              current_level: newUser.currentLevel,
              is_admin: newUser.isAdmin,
              has_seen_guide: newUser.hasSeenGuide
            }, { 
              onConflict: 'user_id',
              ignoreDuplicates: false
            });
            
          if (upsertError) {
            console.error('Error upserting profile:', upsertError);
            
            // If all database operations fail, at least update localStorage
            localStorage.setItem('targetLanguage', newUser.targetLanguage);
            localStorage.setItem('nativeLanguage', newUser.nativeLanguage);
            localStorage.setItem('currentLevel', newUser.currentLevel || 'A1');
            localStorage.setItem('hasSeenGuide', String(newUser.hasSeenGuide));
          }
        }
          
        // Store preferences in localStorage after database update
        localStorage.setItem('targetLanguage', newUser.targetLanguage);
        localStorage.setItem('nativeLanguage', newUser.nativeLanguage);
        localStorage.setItem('currentLevel', newUser.currentLevel || 'A1');
        localStorage.setItem('hasSeenGuide', String(newUser.hasSeenGuide));
        
        setCurrentView('lessons');
        setShowContinuePrompt(true); // Add this line to show the continue prompt
        
        // Show guide if user hasn't seen it
        if (!newUser.hasSeenGuide) {
          setShowGuide(true);
        }
      } catch (error) {
        console.error('Error updating profile:', error);
        
        // If database operations fail, at least update localStorage
        localStorage.setItem('targetLanguage', newUser.targetLanguage);
        localStorage.setItem('nativeLanguage', newUser.nativeLanguage);
        localStorage.setItem('currentLevel', newUser.currentLevel || 'A1');
        localStorage.setItem('hasSeenGuide', String(newUser.hasSeenGuide));
        
        setCurrentView('lessons');
        setShowContinuePrompt(true); // Add this line to show the continue prompt
      }
    } catch (error) {
      console.error('Error setting up user preferences:', error);
      setCurrentView('lessons');
      setShowContinuePrompt(true); // Add this line to show the continue prompt
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error signing out:', error);
        throw error;
      }
      
      setUser(null);
      setCurrentView('login');
      
      localStorage.clear();
      
      // Allow reinitialization after sign out
      authInitializedRef.current = false;
    } catch (error) {
      console.error('Error signing out:', error);
      // Continue with local cleanup even if server-side signout fails
      setUser(null);
      
      setCurrentView('login');
      localStorage.clear();
      
      // Allow reinitialization after sign out
      authInitializedRef.current = false;
    }
  };

  const handleLanguageSelect = async (targetLang: string, nativeLang: string) => {
    if (user) {
      try {
        // Store in localStorage for immediate effect
        localStorage.setItem('targetLanguage', targetLang);
        localStorage.setItem('nativeLanguage', nativeLang);

        // Try to update the profile, but don't block on it
        try {
          const { error } = await supabase
            .from('profiles')
            .update({ 
              target_language: targetLang,
              native_language: nativeLang
            })
            .eq('user_id', user.uid);
          
          if (error) {
            console.error('Error updating language preferences:', error);
            
            // Try upsert if update fails
            const { error: upsertError } = await supabase
              .from('profiles')
              .upsert({
                user_id: user.uid,
                email: user.email,
                target_language: targetLang,
                native_language: nativeLang
              }, {
                onConflict: 'user_id',
                ignoreDuplicates: false
              });
              
            if (upsertError) {
              console.error('Error upserting language preferences:', upsertError);
            }
          }
        } catch (dbError) {
          console.error('Database error when updating language preferences:', dbError);
        }

        setUser({
          ...user,
          targetLanguage: targetLang,
          nativeLanguage: nativeLang
        });
        setCurrentView('level');
      } catch (error) {
        console.error('Error updating language preferences:', error);
      }
    }
  };

  const handleLevelSelect = async (level: LanguageLevel) => {
    if (user) {
      try {
        console.log('Setting level to:', level, 'for user:', user.uid, 'with email:', user.email);
        
        // Update localStorage immediately for responsive UI
        localStorage.setItem('currentLevel', level);
        
        // Update the profile in the database - source of truth
        try {
          const { error } = await supabase 
            .from('profiles')
            .update({ 
              current_level: level,
              updated_at: new Date().toISOString()
            })
            .eq('user_id', user.uid);

          if (error) {
            console.error('Error updating user level in database:', error);
            
            // If update fails, try upsert
            const { error: upsertError } = await supabase
              .from('profiles')
              .upsert({
                user_id: user.uid,
                email: user.email,
                current_level: level,
                target_language: user.targetLanguage,
                native_language: user.nativeLanguage,
                is_admin: user.isAdmin || false,
                has_seen_guide: user.hasSeenGuide || false
              }, {
                onConflict: 'user_id',
                ignoreDuplicates: false
              });
              
            if (upsertError) {
              console.error('Error upserting user profile:', upsertError);
            }
          }
          
          // Update state after database update
          setUser({
            ...user,
            currentLevel: level
          });
          
          setCurrentView('lessons');
          setShowContinuePrompt(true); // Add this line to show continue prompt after level change
        } catch (dbError) {
          console.error('Database error when updating level:', dbError);
          
          // If database operation fails, at least update state and localStorage
          setUser({
            ...user,
            currentLevel: level
          });
          
          setCurrentView('lessons');
          setShowContinuePrompt(true); // Add this line to show continue prompt after level change
        }
      } catch (error) {
        console.error('Error updating user level:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="h-12 w-12 rounded-full border-4 border-indigo-500 border-t-transparent animate-spin"></div>
          <div className="text-indigo-600 font-medium">Loading...</div>
          <div className="text-sm text-gray-500 max-w-xs text-center">
            If loading persists for more than 10 seconds, please refresh the page.
          </div>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <AppContent
        user={user}
        currentView={currentView}
        setCurrentView={setCurrentView}
        showContinuePrompt={showContinuePrompt}
        setShowContinuePrompt={setShowContinuePrompt}
        showGuide={showGuide}
        setShowGuide={setShowGuide}
        isLevelLoading={isLevelLoading}
        pageKey={pageKey}
        handleLanguageSelect={handleLanguageSelect}
        handleLevelSelect={handleLevelSelect}
        handleSignOut={handleSignOut}
        handleLogin={handleLogin}
      />
    </Router>
  );
}

export default App;